/**
 * Email validation utilities
 * Used for basic email validation in signup and user management
 */

/**
 * Canonical form of an email address for authentication: trimmed + lowercased.
 *
 * Mirrors normalize_email() in the backend's encryption_service.py. Email is
 * case-insensitive for delivery, but a WeaveIn account stored as `kamea@…` was
 * unreachable when the address was typed `Kamea@…` — which is how our own
 * invitation emails rendered it, and what a phone keyboard produces by
 * autocapitalizing the first letter. Login answered "invalid credentials" and
 * password reset silently sent nothing (TRUS-386).
 *
 * Use on every field that identifies an account (login, password reset, invite
 * acceptance). Never use on a password — case and whitespace matter there.
 *
 * @param email - Email address as typed by a user
 * @returns Trimmed, lowercased email ('' for empty input)
 */
export function normalizeEmail(email: string): string {
  if (!email || typeof email !== 'string') {
    return '';
  }

  return email.trim().toLowerCase();
}

/**
 * Extract organization name from email domain
 * @param email - The email address
 * @returns Suggested organization name based on domain
 * @example
 * extractOrgNameFromEmail('john@acmehealth.com') // returns 'Acme Health'
 */
export function extractOrgNameFromEmail(email: string): string {
  if (!email || typeof email !== 'string') {
    return '';
  }

  const domain = email.toLowerCase().split('@')[1];
  if (!domain) {
    return '';
  }

  // Remove TLD (.com, .org, etc.)
  const domainName = domain.split('.')[0];

  // Convert to title case and handle common patterns
  return capitalizeWords(domainName);
}

/**
 * Convert a string to title case, handling special characters
 * @param str - The string to capitalize
 * @returns Title cased string
 */
function capitalizeWords(str: string): string {
  return str
    .replace(/[-_]/g, ' ') // Replace hyphens and underscores with spaces
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get a user-friendly error message for invalid email
 * @param email - The email address
 * @returns Error message or null if valid
 */
export function getEmailValidationError(email: string): string | null {
  if (!email) {
    return 'Email is required';
  }

  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address';
  }

  return null;
}

/**
 * Validate if email domain might be a valid organization domain
 * Basic checks for domain validity
 * @param email - The email address
 * @returns true if domain seems valid for an organization
 */
export function hasValidOrgDomain(email: string): boolean {
  const domain = email.toLowerCase().split('@')[1];
  if (!domain) {
    return false;
  }

  // Domain should have at least one dot and valid TLD
  const parts = domain.split('.');
  if (parts.length < 2) {
    return false;
  }

  // TLD should be at least 2 characters
  const tld = parts[parts.length - 1];
  if (tld.length < 2) {
    return false;
  }

  return true;
}
